import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { tagsColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { TagService } from '../../services/entity-services/tag.service'
import { TagType } from 'depoto-core/dist/src/entities/TagType'

const DEFAULT_FILTERS = {
  name: '',
  type: 'all',
}

const tagSchema = {
  id: null,
  name: null,
  type: null,
  color: null,
}

@Component({
  selector: 'tags',
  templateUrl: './tags.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TagsComponent extends BaseListing implements OnInit {
  private tagService = inject(TagService)

  listingColumns = signal(tagsColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(
    this.loadViewSettings('tags')?.filters ? this.loadViewSettings('tags').filters : structuredClone(DEFAULT_FILTERS)
  )
  // filters = signal(this.loadViewSettings('tags')?.filters ? this.loadViewSettings('tags').filters : structuredClone(DEFAULT_FILTERS))
  // filters = signal(structuredClone(DEFAULT_FILTERS))
  tagTypes = computed(() => {
    const t = [...(this.queryTagTypes.data() ?? [])] as TagType[]
    t.unshift({ id: 'all', name: 'all' })
    return t
  })

  // Stable key for filters to avoid new object reference on each compute
  filtersKey = computed(() => JSON.stringify(this.queryFilters()))

  queryFilters = computed(() => {
    const f: Record<string, any> = {}
    const value = this.filters()

    if (value.name?.length) f['name'] = value.name
    if (value?.type !== 'all') f['type'] = value?.type

    return f
  })

  readonly queryTagTypes = injectQuery(() => ({
    queryKey: ['tagTypes', 'options'],
    queryFn: async () => {
      return await this.tagService.getTypes({}, { id: null, name: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryTags = injectInfiniteQuery(() => ({
    queryKey: ['tags', this.sortProp(), this.sortDir()],
    queryFn: async ({ pageParam }) => {
      return await this.tagService
        .getTags(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          tagSchema
        )
        .then(result => {
          this.saveViewSettings('tags', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
    // onSuccess: data => {},
  }))

  totalItems = computed(() => this.queryTags.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryTagTypes.data()
      this.queryTags.data()
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('tags')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('tags')
  }

  public clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryTags.refetch()
  }

  // Immutable updates for filters so computeds re-run and queryKey changes
  onNameFilterChange(name: string) {
    this.filters.update(v => ({ ...v, name: name ?? '' }))
  }

  onTypeFilterChange(type: string) {
    this.filters.update(v => ({ ...v, type: (type ?? 'all') as any }))
  }
}
